import { useState } from 'react';
import type { ReactNode } from 'react';
import { useAction, useApi, useRead } from './context';
import { Confirm, Empty, Failure, Loading, Section, When } from './bits';
import { DomainStatus } from '../wire';
import type { TenantDomain } from '../wire';

/**
 * Where a tenant's booking site answers (spec §6.4.2).
 *
 * "A tenant's 'website' is therefore three rows (`tenant_domains`,
 * `tenant_config` branding, `tenant_modules`) and **zero deployments**." This
 * card is the first of those rows, and it is the screen the spec describes when
 * it says "the admin console shows the domain walking through
 * `pending DNS → issuing → live`".
 *
 * ## The one thing this card must not let anyone believe
 *
 * **Moving the row does not move the world.** The status decides whether
 * `guest-api` *answers* on the hostname; it does not create a DNS record and it
 * does not provision a certificate. Those are the tenant's registrar and
 * Certificate Manager (Terraform, §12). So the sentence is on the card itself,
 * on the confirm dialog, and in the server's own `note` after every change —
 * three places, because the failure it prevents is an operator clicking `live`
 * on a domain whose CNAME has not propagated and then reporting an outage when
 * a browser cannot complete the handshake.
 *
 * ## Two things this card deliberately cannot do
 *
 *  * **Remove a domain.** There is no route and no grant: migration 0013 gives
 *    `admin_api` SELECT, INSERT and UPDATE on `tenant_domains` and no DELETE.
 *    Unresolving a public hostname signs out every guest whose session cookie is
 *    scoped to it (§6.4.1) and 404s every bookmark, so it lives in `apps/cli`
 *    where the hostname is re-typed. The card says so rather than leaving a
 *    reader to wonder where the delete button went.
 *  * **Take a hostname from another tenant.** The server answers `409` and names
 *    the CLI. Same reason, one step further: it would take a *working* site off
 *    the air and point its address at a different business.
 *
 * `Confirm` on every move, quoting the consequence rather than asking whether
 * you are sure — the pattern `TenantDetail`'s lifecycle section established, for
 * the same reason it established it.
 */

const MEANING: Record<string, string> = {
  pending_dns:
    'Waiting on the tenant’s DNS record. guest-api answers 404 on this hostname, so nothing ' +
    'is served yet.',
  issuing:
    'DNS resolves and Certificate Manager is provisioning the managed certificate. Still not ' +
    'served — this status records the wait, it does not shorten it.',
  live: 'guest-api resolves this hostname to the tenant and serves their site on it.',
};

const CONSEQUENCE: Record<string, string> = {
  pending_dns:
    'The hostname stops resolving to this tenant. Every guest signed in on it is signed out ' +
    '(session cookies are scoped to the hostname, spec §6.4.1) and every bookmark 404s. ' +
    'Nothing else about the tenant changes — its book, catalog and guests are untouched.',
  issuing:
    'The hostname stops being served while the certificate is provisioned. Guests signed in ' +
    'on it are signed out. This is the honest state for a domain whose certificate is not ' +
    'yet serving; setting it is not what makes Certificate Manager start.',
  live:
    'guest-api starts answering on this hostname. Set this only once the certificate is ' +
    'actually serving: if it is not, a browser fails the TLS handshake before it ever reaches ' +
    'us, which looks like an outage and is a status set too early.',
};

export function TenantDomains({ tenantId }: { tenantId: string }): ReactNode {
  const api = useApi();
  const add = useAction();
  const move = useAction();

  const [adding, setAdding] = useState(false);
  const [hostname, setHostname] = useState('');
  const [note, setNote] = useState<string | null>(null);
  const [pending, setPending] = useState<{ domain: TenantDomain; status: string } | null>(null);

  const page = useRead(() => api.domains(tenantId), [tenantId]);

  const submit = async (): Promise<void> => {
    const ok = await add.run(async () => {
      const answer = await api.addDomain(tenantId, { hostname });
      setNote(
        `${answer.created === true ? 'Added' : 'Updated'} ${answer.hostname} — ${answer.meaning} ` +
          answer.note,
      );
    });
    if (ok) {
      setAdding(false);
      setHostname('');
      page.reload();
    }
  };

  const confirmMove = async (): Promise<void> => {
    if (pending === null) return;
    const ok = await move.run(async () => {
      const answer = await api.setDomainStatus(
        tenantId,
        pending.domain.hostname,
        DomainStatus.parse(pending.status),
      );
      setNote(
        `${answer.hostname}: ${answer.previous_status ?? '—'} → ${answer.status}. ` +
          `${answer.meaning} ${answer.note}`,
      );
    });
    if (ok) {
      setPending(null);
      page.reload();
    }
  };

  return (
    <Section
      title="Domains"
      note="A tenant’s website is three rows and zero deployments (spec §6.4.2). This card owns one of them — it decides whether guest-api answers on a hostname, not what DNS says."
      actions={
        <button
          type="button"
          className="button button--primary"
          onClick={() => {
            add.clear();
            setAdding((v) => !v);
          }}
        >
          Add domain
        </button>
      }
    >
      {note !== null ? <p className="ok">{note}</p> : null}
      {!adding && add.failure !== null ? <Failure failure={add.failure} /> : null}
      {pending === null && move.failure !== null ? <Failure failure={move.failure} /> : null}
      {page.failure !== null ? <Failure failure={page.failure} /> : null}

      {adding ? (
        <form
          className="form"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <label className="field field--wide">
            <span className="field__label">Hostname</span>
            <input
              value={hostname}
              onChange={(e) => setHostname(e.target.value)}
              placeholder="book.lovespa.com"
              aria-label="Hostname"
            />
            <span className="field__hint">
              The tenant points this at our load balancer with one DNS record — a CNAME, or A/AAAA
              to the static IP for an apex. It lands at <code>pending_dns</code>: adding it here
              does not create the record and does not provision the certificate.
            </span>
          </label>

          {add.failure !== null ? <Failure failure={add.failure} /> : null}

          <div className="row-actions">
            <button type="button" className="button" onClick={() => setAdding(false)}>
              Cancel
            </button>
            <button
              type="submit"
              className="button button--primary"
              disabled={add.busy || hostname.trim() === ''}
            >
              {add.busy ? 'Saving…' : 'Save domain'}
            </button>
          </div>
        </form>
      ) : null}

      {page.value === null ? (
        page.loading ? (
          <Loading what="the tenant’s domains" />
        ) : null
      ) : page.value.domains.length === 0 ? (
        <Empty>
          No custom hostname. The booking site is still reachable at the zero-setup{' '}
          <code>{'{slug}'}.book.yourstore.&lt;tld&gt;</code> default, which needs no row at all.
        </Empty>
      ) : (
        <table className="table table--dense">
          <thead>
            <tr>
              <th>Hostname</th>
              <th>Kind</th>
              <th>Status</th>
              <th>Served</th>
              <th>Verified</th>
              <th>Move to</th>
            </tr>
          </thead>
          <tbody>
            {page.value.domains.map((d) => (
              <tr key={d.hostname}>
                <td className="wrap">
                  <code>{d.hostname}</code>
                </td>
                <td className="muted">{d.kind}</td>
                <td>
                  <span className={`pill pill--domain-${d.status}`}>{d.status}</span>
                </td>
                <td>
                  {/*
                   * The column an operator is actually here for, spelled out
                   * rather than inferred from the status name: `live` is the
                   * only status `app.tenant_for_host` resolves, and `issuing`
                   * reads like "nearly working" to anyone who has not read the
                   * migration.
                   */}
                  {d.served ? 'yes' : <span className="muted">no</span>}
                </td>
                <td>
                  <When iso={d.verified_at} empty="—" />
                </td>
                <td>
                  <div className="row-actions">
                    {DomainStatus.options
                      .filter((s) => s !== d.status)
                      .map((s) => (
                        <button
                          key={s}
                          type="button"
                          className="linkish"
                          onClick={() => {
                            move.clear();
                            setNote(null);
                            setPending({ domain: d, status: s });
                          }}
                        >
                          {s}
                        </button>
                      ))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <p className="muted">
        Removing a domain, and taking one from another tenant, are <code>apps/cli</code>’s:{' '}
        <code>yourstore domain remove --tenant … --hostname … --confirm …</code>. Both unresolve a
        public address, so they are deliberately not one click here — the <code>admin_api</code>{' '}
        role holds no <code>DELETE</code> on <code>tenant_domains</code> (migration 0013), which is
        the same lock from underneath.
      </p>

      {pending !== null ? (
        <Confirm
          title={`Move ${pending.domain.hostname} to ${pending.status}?`}
          consequence={`${CONSEQUENCE[pending.status] ?? ''} ${
            MEANING[pending.status] ?? ''
          } This changes a database row, not the internet: the DNS record is the tenant’s registrar’s and the certificate is Certificate Manager’s.`}
          confirmLabel={`Move to ${pending.status}`}
          busy={move.busy}
          failure={move.failure}
          onConfirm={() => void confirmMove()}
          onCancel={() => {
            move.clear();
            setPending(null);
          }}
        />
      ) : null}
    </Section>
  );
}
